import { getEntitySlug } from "./entitySlug";

interface PaginatedResponse<T> {
  items: T[];
  meta?: {
    skip: number;
    take: number;
    total: number;
  };
  error?: string;
}

export interface ProjectSummary {
  id: string | number;
  name?: string;
  code?: string;
  customerId?: string | number;
  customer?: unknown;
}

export interface ProjectDetail extends ProjectSummary {
  description?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  createdAt?: string;
  updatedAt?: string;
  context?: string | null;
  contextSources?: unknown;
  contextGeneratedAt?: string | null;
  contextGeneratedBy?: string | null;
  contextGenerationStatus?: string;
  contextGenerationError?: string | null;
  preDiscoveryQuestions?: unknown;
  preDiscoveryQuestionsGeneratedAt?: string | null;
  preDiscoveryQuestionsGeneratedBy?: string | null;
  preDiscoveryQuestionsStatus?: string;
  preDiscoveryQuestionsError?: string | null;
}

const PROJECT_ENTITY = getEntitySlug("Project");

type ProjectListResponse = PaginatedResponse<ProjectSummary>;
type ProjectDetailResponse = ProjectDetail & { error?: string };

async function readJsonSafely<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

function normalizeLookupValue(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value).trim().toLowerCase() : "";
}

async function fetchProjectsPage(page: number, pageSize: number): Promise<ProjectListResponse> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const response = await fetch(`/api/entities/${PROJECT_ENTITY}?${params.toString()}`);
  const payload = await readJsonSafely<ProjectListResponse>(response);
  if (!response.ok) {
    const message = payload?.error ?? `Failed to load projects (status ${response.status})`;
    throw new Error(message);
  }
  return payload ?? { items: [], meta: { skip: page * pageSize, take: pageSize, total: 0 } };
}

async function listTenantProjects(maxPages = 20, pageSize = 200): Promise<ProjectSummary[]> {
  const projects: ProjectSummary[] = [];

  for (let page = 0; page < maxPages; page += 1) {
    const payload = await fetchProjectsPage(page, pageSize);
    const items = Array.isArray(payload.items) ? payload.items : [];
    projects.push(...items);

    const total = payload.meta?.total;
    const skip = payload.meta?.skip ?? page * pageSize;
    const take = payload.meta?.take ?? pageSize;
    const reachedEndByMeta = typeof total === "number" && skip + take >= total;
    const reachedEndByPage = items.length < pageSize;

    if (reachedEndByMeta || reachedEndByPage) {
      break;
    }
  }

  return projects;
}

export interface ResolvedProjectReference {
  projectId: string;
  projectName: string | null;
  source: "id" | "name" | "code";
}

export async function fetchProjectsForCustomer(customerId: string | number): Promise<ProjectSummary[]> {
  if (!customerId) return [];

  const params = new URLSearchParams({
    page: "0",
    pageSize: "1000",
    filters: JSON.stringify({ customerId }),
  });

  const response = await fetch(`/api/entities/${PROJECT_ENTITY}?${params.toString()}`);
  const payload = await readJsonSafely<PaginatedResponse<ProjectSummary>>(response);

  if (!response.ok) {
    const message = payload?.error ?? `Failed to load projects (status ${response.status})`;
    throw new Error(message);
  }

  return payload?.items ?? [];
}

export async function fetchProjectById(projectId: string | number): Promise<ProjectDetail> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const response = await fetch(`/api/entities/${PROJECT_ENTITY}/${encodeURIComponent(projectId)}`);
  const payload = await readJsonSafely<ProjectDetailResponse>(response);

  if (!response.ok) {
    const message = payload?.error ?? `Failed to load project (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload) {
    throw new Error("Failed to parse project response");
  }

  return payload;
}

export async function resolveProjectReference(projectReference: string): Promise<ResolvedProjectReference> {
  const reference = projectReference.trim();
  if (!reference) {
    throw new Error("Project name or ID is required");
  }

  const byIdResponse = await fetch(`/api/entities/${PROJECT_ENTITY}/${encodeURIComponent(reference)}`);
  const byIdPayload = await readJsonSafely<ProjectDetailResponse>(byIdResponse);
  if (byIdResponse.ok && byIdPayload) {
    return {
      projectId: String(byIdPayload.id),
      projectName: byIdPayload.name ?? null,
      source: "id",
    };
  }
  if (byIdResponse.status !== 404) {
    const message = byIdPayload?.error ?? `Failed to resolve project (status ${byIdResponse.status})`;
    throw new Error(message);
  }

  const projects = await listTenantProjects();
  const normalizedReference = normalizeLookupValue(reference);

  const exactMatches = projects.filter((project) => {
    const values = [project.id, project.name, project.code];
    return values.some((value) => normalizeLookupValue(value) === normalizedReference);
  });

  if (exactMatches.length === 1) {
    const match = exactMatches[0];
    return {
      projectId: String(match.id),
      projectName: match.name ?? null,
      source: normalizeLookupValue(match.name) === normalizedReference ? "name" : "code",
    };
  }

  if (exactMatches.length > 1) {
    throw new Error(`Project name "${reference}" matches multiple projects. Use a unique project ID.`);
  }

  const partialMatches = projects.filter((project) => {
    const values = [project.name, project.code];
    return values.some((value) => normalizeLookupValue(value).includes(normalizedReference));
  });

  if (partialMatches.length === 1) {
    const match = partialMatches[0];
    return {
      projectId: String(match.id),
      projectName: match.name ?? null,
      source: normalizeLookupValue(match.name).includes(normalizedReference) ? "name" : "code",
    };
  }

  if (partialMatches.length > 1) {
    throw new Error(`Project name "${reference}" is ambiguous. Use the exact project ID.`);
  }

  throw new Error(`Project "${reference}" not found. Use a valid project name or ID.`);
}
